import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, Cards, ColumnLayout, ContentLayout, Header,
    KeyValuePairs, Modal, Select, Spinner, SpaceBetween, StatusIndicator,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { alphaAnalysisApi } from '../../client/clients';
import { StockArticleDto, StockPriceBar, SpotPriceResponse } from '../../api';
import { GetArticleDetailsCredentialKeyEnum } from '../../api';
import { useArticlePopup, InlineSpinner } from './ArticlePopupModal';
import { PublisherMetricsMiniChart } from './PublisherMetricsMiniChart';
import { PublisherOverallBadge } from './PublisherOverallBadge';
import { PriceSnapshotMiniChart } from './PriceSnapshotMiniChart';
import { usePublisherMetrics } from './usePublisherMetrics';
import { computeForecast, ForecastResult } from './forecastUtils';
import {
    createChart, CandlestickSeries, LineSeries,
    createSeriesMarkers, ColorType, IChartApi, LineStyle,
} from 'lightweight-charts';
import type { ISeriesApi, ISeriesMarkersPluginApi, SeriesMarker, Time } from 'lightweight-charts';

// ─── types ────────────────────────────────────────────────────────────────────

interface ChartPoint {
    date: string;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
    stanceAvg?: number;
    articles: StockArticleDto[];
}

interface WindowSeries {
    windowType: string;
    data: { time: Time; value: number }[];
}

interface VisibleSeries {
    ARTICLES: boolean;
    stanceAvg: boolean;
    DAILY: boolean;
    WEEKLY: boolean;
    MONTHLY: boolean;
    FORECAST: boolean;
}

// ─── constants ────────────────────────────────────────────────────────────────

const WINDOW_OPTIONS = [
    { label: '1M', value: '1' },
    { label: '3M', value: '3' },
    { label: '6M', value: '6' },
    { label: '1Y', value: '12' },
];

const WINDOW_DAYS: Record<string, number> = { DAILY: 1, WEEKLY: 7, MONTHLY: 30 };

const AGG_LINE_STYLES: Record<string, { color: string; title: string }> = {
    DAILY:   { color: '#f39c12', title: 'Daily wtd avg' },
    WEEKLY:  { color: '#3498db', title: 'Weekly wtd avg' },
    MONTHLY: { color: '#2ecc71', title: 'Monthly wtd avg' },
};

const ALL_TOGGLE_METRICS = [
    { key: 'ARTICLES' as const, label: 'Article markers', color: '#5f6b7a' },
    { key: 'stanceAvg' as const, label: '30d stance avg',  color: '#8884d8' },
    { key: 'DAILY'    as const, label: 'Daily wtd avg',   color: '#f39c12' },
    { key: 'WEEKLY'   as const, label: 'Weekly wtd avg',  color: '#3498db' },
    { key: 'MONTHLY'  as const, label: 'Monthly wtd avg', color: '#2ecc71' },
    { key: 'FORECAST' as const, label: 'Publisher forecast', color: '#8b5cf6' },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function stanceColor(s: number): 'success' | 'warning' | 'error' {
    return s >= 20 ? 'success' : s >= -20 ? 'warning' : 'error';
}

function articleMarkerColor(stance?: number): string {
    if (stance == null) return '#aab7b8';
    return stance >= 20 ? '#26a69a' : stance >= -20 ? '#f89c1c' : '#ef5350';
}

function buildWindowSeries(prices: StockPriceBar[], articles: StockArticleDto[]): WindowSeries[] {
    const sortedArticles = [...articles].sort((a, b) =>
        (a.publishedAt ?? '').localeCompare(b.publishedAt ?? ''));

    return Object.entries(WINDOW_DAYS).map(([windowType, days]) => {
        const data = prices
            .map(bar => {
                const d = bar.date ?? '';
                if (!d) return null;
                const dDate = new Date(d);
                const dFrom = new Date(dDate);
                dFrom.setDate(dFrom.getDate() - days);

                const relevant = sortedArticles.filter(a => {
                    const ad = new Date((a.publishedAt ?? '').substring(0, 10));
                    return ad <= dDate && ad >= dFrom;
                });
                if (relevant.length === 0) return null;

                const totalConviction = relevant.reduce((s, a) => s + Number(a.conviction ?? 0), 0);
                const weightedStance = relevant.reduce((s, a) => s + Number(a.stance ?? 0) * Number(a.conviction ?? 0), 0);
                const value = totalConviction > 0
                    ? weightedStance / totalConviction
                    : relevant.reduce((s, a) => s + Number(a.stance ?? 0), 0) / relevant.length;

                return { time: d as Time, value };
            })
            .filter((p): p is { time: Time; value: number } => p !== null);

        return { windowType, data };
    });
}

function buildChartData(prices: StockPriceBar[], articles: StockArticleDto[]): ChartPoint[] {
    // Group ALL articles by date — multiple articles per day are preserved
    const articlesByDate = new Map<string, StockArticleDto[]>();
    for (const a of articles) {
        const d = (a.publishedAt ?? '').substring(0, 10);
        if (!d) continue;
        const existing = articlesByDate.get(d) ?? [];
        existing.push(a);
        articlesByDate.set(d, existing);
    }

    const sortedArticles = [...articles].sort((a, b) =>
        (a.publishedAt ?? '').localeCompare(b.publishedAt ?? ''));

    return prices.map(bar => {
        const d = bar.date ?? '';
        const dDate = new Date(d);
        const dMinus30 = new Date(dDate);
        dMinus30.setDate(dMinus30.getDate() - 30);
        const relevantStances = sortedArticles
            .filter(a => {
                const ad = new Date((a.publishedAt ?? '').substring(0, 10));
                return ad <= dDate && ad >= dMinus30;
            })
            .map(a => Number(a.stance ?? 0));
        const stanceAvg = relevantStances.length > 0
            ? relevantStances.reduce((s, v) => s + v, 0) / relevantStances.length
            : undefined;

        return {
            date: d,
            open:  bar.open  ?? undefined,
            high:  bar.high  ?? undefined,
            low:   bar.low   ?? undefined,
            close: bar.close ?? undefined,
            stanceAvg,
            articles: articlesByDate.get(d) ?? [],
        } satisfies ChartPoint;
    });
}

// ─── Spot-price tag ──────────────────────────────────────────────────────────

const SpotPriceTag: React.FC<{ price: number; asOf: string }> = ({ price, asOf }) => {
    const asOfDate = new Date(asOf);
    const hhmm = asOfDate.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    });
    return (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: '#0972d3', fontFamily: 'monospace' }}>
                ${price.toFixed(2)}
            </span>
            <span style={{ color: '#5f6b7a' }}>as of {hhmm} UTC</span>
        </span>
    );
};

// ─── Chart component ─────────────────────────────────────────────────────────

interface OhlcChartProps {
    chartData: ChartPoint[];
    windowSeries: WindowSeries[];
    visibleSeries: VisibleSeries;
    forecast: ForecastResult;
    spotPrice?: SpotPriceResponse | null;
    onArticleClick: (articleId: string, key: GetArticleDetailsCredentialKeyEnum) => void;
    onMultiArticleClick: (articles: StockArticleDto[]) => void;
}

const OhlcChart: React.FC<OhlcChartProps> = ({ chartData, windowSeries, visibleSeries, forecast, spotPrice, onArticleClick, onMultiArticleClick }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const stanceRef  = useRef<ISeriesApi<'Line'> | null>(null);
    const dailyRef   = useRef<ISeriesApi<'Line'> | null>(null);
    const weeklyRef  = useRef<ISeriesApi<'Line'> | null>(null);
    const monthlyRef = useRef<ISeriesApi<'Line'> | null>(null);
    const forecastRef    = useRef<ISeriesApi<'Line'> | null>(null);
    const forecastHiRef  = useRef<ISeriesApi<'Line'> | null>(null);
    const forecastLoRef  = useRef<ISeriesApi<'Line'> | null>(null);
    const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
    const markersDataRef   = useRef<SeriesMarker<Time>[]>([]);

    // Keep refs current so effects reading them always see the latest value
    const onArticleClickRef      = useRef(onArticleClick);
    const onMultiArticleClickRef = useRef(onMultiArticleClick);
    const visibleSeriesRef       = useRef(visibleSeries);
    useEffect(() => { onArticleClickRef.current      = onArticleClick; },      [onArticleClick]);
    useEffect(() => { onMultiArticleClickRef.current = onMultiArticleClick; }, [onMultiArticleClick]);
    useEffect(() => { visibleSeriesRef.current       = visibleSeries; },       [visibleSeries]);

    const aggRefs: Record<string, React.MutableRefObject<ISeriesApi<'Line'> | null>> = {
        DAILY: dailyRef, WEEKLY: weeklyRef, MONTHLY: monthlyRef,
    };

    // ── Build chart (only when data changes) ─────────────────────────────────
    useEffect(() => {
        if (!containerRef.current) return;
        const container = containerRef.current;
        const vis = visibleSeriesRef.current;

        const chart = createChart(container, {
            width: container.clientWidth,
            height: 360,
            layout: {
                background: { type: ColorType.Solid, color: '#ffffff' },
                textColor: '#414d5c',
            },
            grid: {
                vertLines: { color: '#f4f4f4' },
                horzLines: { color: '#f4f4f4' },
            },
            leftPriceScale:  { visible: true, borderVisible: false },
            rightPriceScale: { visible: true, borderVisible: false },
            timeScale: { borderColor: '#e9ebed', rightOffset: 30 },
            crosshair: { mode: 1 },
        });
        chartRef.current = chart;

        // Candlestick on left scale
        const candle = chart.addSeries(CandlestickSeries, {
            priceScaleId: 'left',
            upColor:         '#26a69a',
            downColor:       '#ef5350',
            borderUpColor:   '#26a69a',
            borderDownColor: '#ef5350',
            wickUpColor:     '#26a69a',
            wickDownColor:   '#ef5350',
        });
        candleRef.current = candle;

        // 30d stance avg on right scale
        const stance = chart.addSeries(LineSeries, {
            priceScaleId: 'right',
            color: '#8884d8',
            lineWidth: 2,
            crosshairMarkerVisible: true,
            lastValueVisible: true,
            priceLineVisible: false,
            visible: vis.stanceAvg,
        });
        chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.6, bottom: 0.05 } });
        stanceRef.current = stance;

        // Click → open article popup; show picker when multiple articles share the date
        chart.subscribeClick(param => {
            if (!param.time) return;
            const t = param.time as string;
            const pt = chartData.find(p => p.date === t);
            const arts = pt?.articles ?? [];
            if (arts.length === 0) return;
            if (arts.length === 1) {
                if (arts[0].articleId) {
                    onArticleClickRef.current(
                        arts[0].articleId,
                        GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie,
                    );
                }
            } else {
                onMultiArticleClickRef.current(arts);
            }
        });

        // Feed OHLC data
        const candleData = chartData
            .filter(p => p.open != null && p.high != null && p.low != null && p.close != null)
            .map(p => ({ time: p.date as Time, open: p.open!, high: p.high!, low: p.low!, close: p.close! }));
        candle.setData(candleData);

        // "Now" price line — thin dashed horizontal at the live spot, gated on
        // a known price (Polygon outage → null → line omitted).
        if (spotPrice?.price != null) {
            candle.createPriceLine({
                price: spotPrice.price,
                color: '#0972d3',
                lineWidth: 1,
                lineStyle: LineStyle.Dashed,
                axisLabelVisible: true,
                title: 'now',
            });
        }

        // Feed stance avg data
        const stanceData = chartData
            .filter(p => p.stanceAvg != null)
            .map(p => ({ time: p.date as Time, value: p.stanceAvg! }));
        stance.setData(stanceData);

        // Client-computed DAILY / WEEKLY / MONTHLY conviction-weighted avg lines
        for (const { windowType, data } of windowSeries) {
            const style = AGG_LINE_STYLES[windowType];
            if (!style || data.length === 0) continue;
            const aggSeries = chart.addSeries(LineSeries, {
                priceScaleId: 'right',
                color: style.color,
                lineWidth: 2,
                lineStyle: 1,
                crosshairMarkerVisible: true,
                lastValueVisible: true,
                priceLineVisible: false,
                title: style.title,
                visible: vis[windowType as keyof VisibleSeries] as boolean,
            });
            aggSeries.setData(data);
            aggRefs[windowType].current = aggSeries;
        }

        // Article markers — one per article so multi-article days show all of them
        const markers: SeriesMarker<Time>[] = chartData
            .flatMap(p => p.articles.map(a => ({
                time:     p.date as Time,
                position: 'belowBar' as const,
                color:    articleMarkerColor(Number(a.stance ?? 0)),
                shape:    'circle' as const,
                text:     '',
            })))
            .sort((a, b) => (a.time as string).localeCompare(b.time as string));

        markersDataRef.current = markers;
        if (markers.length > 0) {
            const plugin = createSeriesMarkers(candle, vis.ARTICLES ? markers : []);
            markersPluginRef.current = plugin;
        }

        // Forecast: dotted purple line + ±1σ bands anchored at the most-recent
        // article. All three render on the LEFT (candle) price scale so they
        // line up with the OHLC bars. Forecast points sit in the future, so
        // we use unix-seconds (lightweight-charts accepts both ISO strings
        // and numbers for `Time`).
        const anchorMs = Date.parse(forecast.anchorTime);
        if (Number.isFinite(anchorMs) && forecast.anchorPrice > 0) {
            const anchorTimeSec = Math.floor(anchorMs / 1000) as unknown as Time;
            const fcMain: { time: Time; value: number }[] = [
                { time: anchorTimeSec, value: forecast.anchorPrice },
                ...forecast.points.map(p => ({
                    time:  Math.floor((anchorMs + p.offsetMs) / 1000) as unknown as Time,
                    value: p.projectedPrice,
                })),
            ];
            const fcHi: { time: Time; value: number }[] = [
                { time: anchorTimeSec, value: forecast.anchorPrice },
                ...forecast.points.map(p => ({
                    time:  Math.floor((anchorMs + p.offsetMs) / 1000) as unknown as Time,
                    value: p.upperPrice,
                })),
            ];
            const fcLo: { time: Time; value: number }[] = [
                { time: anchorTimeSec, value: forecast.anchorPrice },
                ...forecast.points.map(p => ({
                    time:  Math.floor((anchorMs + p.offsetMs) / 1000) as unknown as Time,
                    value: p.lowerPrice,
                })),
            ];

            const forecastSeries = chart.addSeries(LineSeries, {
                priceScaleId: 'left',
                color: '#8b5cf6',
                lineWidth: 2,
                lineStyle: LineStyle.Dotted,
                crosshairMarkerVisible: true,
                lastValueVisible: false,
                priceLineVisible: false,
                title: 'Forecast',
                visible: vis.FORECAST,
            });
            forecastSeries.setData(fcMain);
            forecastRef.current = forecastSeries;

            const forecastHi = chart.addSeries(LineSeries, {
                priceScaleId: 'left',
                color: 'rgba(139,92,246,0.45)',
                lineWidth: 1,
                lineStyle: LineStyle.Dotted,
                crosshairMarkerVisible: false,
                lastValueVisible: false,
                priceLineVisible: false,
                title: '+1σ',
                visible: vis.FORECAST,
            });
            forecastHi.setData(fcHi);
            forecastHiRef.current = forecastHi;

            const forecastLo = chart.addSeries(LineSeries, {
                priceScaleId: 'left',
                color: 'rgba(139,92,246,0.45)',
                lineWidth: 1,
                lineStyle: LineStyle.Dotted,
                crosshairMarkerVisible: false,
                lastValueVisible: false,
                priceLineVisible: false,
                title: '−1σ',
                visible: vis.FORECAST,
            });
            forecastLo.setData(fcLo);
            forecastLoRef.current = forecastLo;
        }

        chart.timeScale().fitContent();

        const ro = new ResizeObserver(entries => {
            chart.applyOptions({ width: entries[0].contentRect.width });
        });
        ro.observe(container);

        return () => {
            ro.disconnect();
            chart.remove();
            chartRef.current  = null;
            candleRef.current = null;
            stanceRef.current = null;
            dailyRef.current  = null;
            weeklyRef.current = null;
            monthlyRef.current = null;
            forecastRef.current   = null;
            forecastHiRef.current = null;
            forecastLoRef.current = null;
            markersPluginRef.current = null;
            markersDataRef.current   = [];
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData, windowSeries, forecast, spotPrice]);

    // ── Toggle visibility without recreating the chart ────────────────────────
    useEffect(() => {
        stanceRef.current?.applyOptions({ visible: visibleSeries.stanceAvg });
        dailyRef.current?.applyOptions({ visible: visibleSeries.DAILY });
        weeklyRef.current?.applyOptions({ visible: visibleSeries.WEEKLY });
        monthlyRef.current?.applyOptions({ visible: visibleSeries.MONTHLY });
        forecastRef.current?.applyOptions({   visible: visibleSeries.FORECAST });
        forecastHiRef.current?.applyOptions({ visible: visibleSeries.FORECAST });
        forecastLoRef.current?.applyOptions({ visible: visibleSeries.FORECAST });
        markersPluginRef.current?.setMarkers(
            visibleSeries.ARTICLES ? markersDataRef.current : []
        );
    }, [visibleSeries]);

    return <div ref={containerRef} style={{ width: '100%' }} />;
};

// ─── Main component ──────────────────────────────────────────────────────────

export const AlphaStockDetailPage: React.FC = () => {
    const { ticker } = useParams<{ ticker: string }>();
    useDocumentTitle(`Alpha — ${ticker}`);
    const navigate = useNavigate();

    const [windowMonths, setWindowMonths] = useState<string>('12');
    const [prices, setPrices] = useState<StockPriceBar[]>([]);
    const [articles, setArticles] = useState<StockArticleDto[]>([]);
    const [spotPrice, setSpotPrice] = useState<SpotPriceResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [visibleSeries, setVisibleSeries] = useState<VisibleSeries>({
        ARTICLES: true,
        stanceAvg: true,
        DAILY: true,
        WEEKLY: true,
        MONTHLY: true,
        FORECAST: true,
    });
    const [pickerArticles, setPickerArticles] = useState<StockArticleDto[] | null>(null);
    const { openArticle, loadingArticleId, modalElement } = useArticlePopup();

    const stockAuthorIds = useMemo(
        () => articles.map(a => a.authorId).filter((id): id is string => !!id),
        [articles]);
    const { metricsByAuthor: stockMetricsByAuthor } = usePublisherMetrics(stockAuthorIds);

    useEffect(() => {
        setLoading(true);
        const months = parseInt(windowMonths, 10);
        const days = months * 30;

        Promise.all([
            alphaAnalysisApi.getStockPrices(ticker!, months),
            alphaAnalysisApi.getStockArticles(ticker!, days),
            // Catch-and-null on spot so a Polygon outage doesn't break the page.
            // The forecast falls back to the latest-article anchor automatically.
            alphaAnalysisApi.getSpotPrice(ticker!).catch(() => ({ data: null as SpotPriceResponse | null })),
        ])
            .then(([pricesResp, articlesResp, spotResp]) => {
                setPrices(pricesResp.data);
                setArticles(articlesResp.data);
                setSpotPrice(spotResp.data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [ticker, windowMonths]);

    const chartData    = useMemo(() => buildChartData(prices, articles), [prices, articles]);
    const windowSeries = useMemo(() => buildWindowSeries(prices, articles), [prices, articles]);
    const forecast     = useMemo(() => computeForecast(articles, stockMetricsByAuthor, {
        // Anchor the projection at the live spot when Polygon gave us a number.
        // Falls back to the latest-article anchor when spot is missing (Polygon
        // outage, unsupported ticker, or the page is loading without spot yet).
        anchor: spotPrice && spotPrice.price != null && spotPrice.asOf
            ? { price: spotPrice.price, timeMs: Date.parse(spotPrice.asOf) }
            : undefined,
    }), [articles, stockMetricsByAuthor, spotPrice]);

    const handleOpenArticle = useCallback((articleId: string, key: GetArticleDetailsCredentialKeyEnum) => {
        openArticle(articleId, key);
    }, [openArticle]);

    const handleMultiArticleClick = useCallback((arts: StockArticleDto[]) => {
        setPickerArticles(arts);
    }, []);

    const toggleSeries = useCallback((key: keyof VisibleSeries) => {
        setVisibleSeries(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const latestArticle = articles[0];

    if (loading) return <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>;

    return (
        <ContentLayout
            header={
                <Header
                    variant="h1"
                    description={
                        spotPrice?.price != null && spotPrice.asOf
                            ? <SpotPriceTag price={spotPrice.price} asOf={spotPrice.asOf} />
                            : undefined
                    }
                    actions={
                        <Select
                            selectedOption={WINDOW_OPTIONS.find(o => o.value === windowMonths) ?? WINDOW_OPTIONS[1]}
                            onChange={e => setWindowMonths(e.detail.selectedOption.value ?? '3')}
                            options={WINDOW_OPTIONS}
                        />
                    }
                >
                    {ticker} — Stock Analysis
                </Header>
            }
        >
            <SpaceBetween size="l">
                {latestArticle && (
                    <ColumnLayout columns={3} variant="text-grid">
                        <KeyValuePairs items={[{
                            label: 'Latest Stance',
                            value: (
                                <StatusIndicator type={stanceColor(Number(latestArticle.stance ?? 0))}>
                                    {Number(latestArticle.stance ?? 0) > 0 ? '+' : ''}{latestArticle.stance ?? 0}
                                </StatusIndicator>
                            ),
                        }]} />
                        <KeyValuePairs items={[{
                            label: 'Conviction',
                            value: latestArticle.conviction != null ? `${latestArticle.conviction}/100` : 'N/A',
                        }]} />
                        <KeyValuePairs items={[{
                            label: 'Price at Publish',
                            value: latestArticle.priceAtPublish != null ? `$${latestArticle.priceAtPublish.toFixed(2)}` : 'N/A',
                        }]} />
                    </ColumnLayout>
                )}

                {/* OHLC chart */}
                <div style={{ background: '#fff', border: '1px solid #e9ebed', borderRadius: 8, padding: 16 }}>
                    {chartData.length > 0 ? (
                        <>
                            {/* Toggle controls */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                {ALL_TOGGLE_METRICS.map(({ key, label, color }) => {
                                    const active = visibleSeries[key];
                                    const isArticles = key === 'ARTICLES';
                                    const isForecast = key === 'FORECAST';
                                    const muted     = isForecast && !forecast.hasSignal;
                                    const title     = muted ? 'Not enough analyst-score data yet' : undefined;
                                    // When muted, dim the chip even in active state so the
                                    // "we have no signal" hint is visible. Click still works
                                    // (the band still renders at the anchor).
                                    const borderColor = muted ? '#d1d5db' : (active ? color : '#d1d5db');
                                    const bgColor     = muted ? 'transparent' : (active ? color : 'transparent');
                                    const textColor   = muted ? '#9ca3af' : (active ? '#fff' : '#9ca3af');
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => toggleSeries(key)}
                                            title={title}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 5,
                                                padding: '3px 10px',
                                                borderRadius: 12,
                                                border: `1px solid ${borderColor}`,
                                                background: bgColor,
                                                color: textColor,
                                                cursor: 'pointer',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                fontFamily: 'inherit',
                                                opacity: muted ? 0.6 : 1,
                                                transition: 'background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s',
                                            }}
                                        >
                                            {isArticles ? (
                                                <span style={{ display: 'inline-flex', gap: 1, fontSize: 10 }}>
                                                    <span style={{ color: active ? 'rgba(255,255,255,0.9)' : '#26a69a' }}>●</span>
                                                    <span style={{ color: active ? 'rgba(255,255,255,0.9)' : '#f89c1c' }}>●</span>
                                                    <span style={{ color: active ? 'rgba(255,255,255,0.9)' : '#ef5350' }}>●</span>
                                                </span>
                                            ) : (
                                                <span style={{ fontSize: 14, lineHeight: 1, color: muted ? '#9ca3af' : (active ? '#fff' : color) }}>—</span>
                                            )}
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            <OhlcChart
                                chartData={chartData}
                                windowSeries={windowSeries}
                                visibleSeries={visibleSeries}
                                forecast={forecast}
                                spotPrice={spotPrice}
                                onArticleClick={handleOpenArticle}
                                onMultiArticleClick={handleMultiArticleClick}
                            />
                        </>
                    ) : (
                        <Box textAlign="center" color="text-status-inactive">
                            No price data available for {ticker} in this window.
                        </Box>
                    )}
                </div>

                {/* Article cards */}
                <Cards
                    items={articles}
                    cardDefinition={{
                        header: (item: StockArticleDto) => {
                            const artId = item.articleId ?? '';
                            const isLoading = loadingArticleId === artId;
                            return (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <button
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0972d3', textAlign: 'left', fontSize: 'inherit', fontWeight: 'inherit' }}
                                        onClick={() => artId && openArticle(artId, GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie)}
                                        disabled={isLoading || !artId}
                                    >
                                        {item.articleTitle ?? ''}
                                    </button>
                                    <InlineSpinner show={isLoading} />
                                </span>
                            );
                        },
                        sections: [
                            {
                                id: 'date',
                                header: 'Published',
                                content: (item: StockArticleDto) => (item.publishedAt ?? '').substring(0, 10),
                            },
                            {
                                id: 'author',
                                header: 'Analyst',
                                content: (item: StockArticleDto) => (
                                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                                        <span
                                            style={{ cursor: 'pointer', color: '#0972d3' }}
                                            onClick={() => navigate(`/alpha/analysts/${item.authorId}`)}
                                        >
                                            {item.authorName}
                                        </span>
                                        {item.authorId && (
                                            <>
                                                <PublisherOverallBadge metrics={stockMetricsByAuthor[item.authorId]} />
                                                <PublisherMetricsMiniChart
                                                    metrics={stockMetricsByAuthor[item.authorId]}
                                                    authorName={item.authorName}
                                                />
                                            </>
                                        )}
                                    </SpaceBetween>
                                ),
                            },
                            {
                                id: 'stance',
                                header: 'Stance',
                                content: (item: StockArticleDto) => {
                                    const s = Number(item.stance ?? 0);
                                    return (
                                        <StatusIndicator type={stanceColor(s)}>
                                            {s > 0 ? '+' : ''}{item.stance ?? 0}
                                        </StatusIndicator>
                                    );
                                },
                            },
                            {
                                id: 'conviction',
                                header: 'Conviction',
                                content: (item: StockArticleDto) =>
                                    item.conviction != null ? `${item.conviction}/100` : null,
                            },
                            {
                                id: 'price',
                                header: 'Price at Publish',
                                content: (item: StockArticleDto) =>
                                    item.priceAtPublish != null ? `$${item.priceAtPublish.toFixed(2)}` : 'N/A',
                            },
                            {
                                id: 'forwardPrices',
                                header: 'Forward Price Trajectory',
                                content: (item: StockArticleDto) => (
                                    <PriceSnapshotMiniChart article={item} />
                                ),
                            },
                        ],
                    }}
                    trackBy="articleId"
                    empty={<Box textAlign="center">No articles for {ticker} in this window</Box>}
                />

                {pickerArticles && (
                    <Modal
                        visible={true}
                        onDismiss={() => setPickerArticles(null)}
                        header={`${pickerArticles.length} articles on ${(pickerArticles[0]?.publishedAt ?? '').substring(0, 10)}`}
                        size="medium"
                        footer={
                            <Box float="right">
                                <Button onClick={() => setPickerArticles(null)}>Cancel</Button>
                            </Box>
                        }
                    >
                        <SpaceBetween size="xs">
                            {pickerArticles.map(a => {
                                const artId = a.articleId ?? '';
                                const isLoading = loadingArticleId === artId;
                                const s = Number(a.stance ?? 0);
                                return (
                                    <div
                                        key={artId}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            padding: '10px 0',
                                            borderBottom: '1px solid #e9ebed',
                                        }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500, fontSize: 14 }}>{a.articleTitle ?? 'Untitled'}</div>
                                            <div style={{ fontSize: 12, color: '#5f6b7a', marginTop: 2 }}>
                                                {a.authorName} · Stance: {s > 0 ? '+' : ''}{s} · Conviction: {a.conviction ?? 'N/A'}
                                            </div>
                                        </div>
                                        <Button
                                            variant="link"
                                            loading={isLoading}
                                            disabled={!artId || isLoading}
                                            onClick={() => {
                                                setPickerArticles(null);
                                                openArticle(artId, GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie);
                                            }}
                                        >
                                            Open
                                        </Button>
                                    </div>
                                );
                            })}
                        </SpaceBetween>
                    </Modal>
                )}

                {modalElement}
            </SpaceBetween>
        </ContentLayout>
    );
};
